/**
 * "Install the app" support.
 *
 * Chrome, Edge and Samsung Internet (Android and desktop) let a page offer the install prompt
 * itself: the browser fires `beforeinstallprompt` once, early, and the page may keep the event and
 * call `prompt()` from a button. It is captured at start-up (initInstallCapture) precisely because
 * it fires before any screen has mounted. Safari on iPhone and iPad has no such API: installing
 * there is Share > Add to Home Screen, which can only be explained, not triggered.
 */
import { useSyncExternalStore } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type Platform = "ios" | "android" | "desktop";

export interface InstallState {
  /** The browser has offered to install and the page may show its own button. */
  canPrompt: boolean;
  /** The user installed it during this visit. */
  installed: boolean;
}

let state: InstallState = { canPrompt: false, installed: false };
let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function update(next: Partial<InstallState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

/** Call once at start-up, before the first render. */
export function initInstallCapture(): void {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // keep it for our own button instead of the browser's mini-bar
    deferred = event as BeforeInstallPromptEvent;
    update({ canPrompt: true });
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    update({ canPrompt: false, installed: true });
  });
}

/** Test hook: forget everything captured so far. */
export function resetInstallState(): void {
  deferred = null;
  state = { canPrompt: false, installed: false };
  for (const listener of listeners) listener();
}

/** Shows the browser's install dialog. The captured event can be used once. */
export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const event = deferred;
  if (!event) return "unavailable";
  deferred = null;
  update({ canPrompt: false });
  await event.prompt();
  return (await event.userChoice).outcome;
}

export function detectPlatform(userAgent: string, maxTouchPoints = 0): Platform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  // iPadOS reports itself as a Mac; a Mac with a touch screen is an iPad.
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "desktop";
}

/** True when running as the installed app (from the home screen), not in a browser tab. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const displayMode = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  return displayMode || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useInstallState = (): InstallState =>
  useSyncExternalStore(subscribe, () => state, () => state);
